/**
 * StatusBreakdownCard - Displays risks by status with progress bars
 * 
 * Features:
 * - Consistent ordering via STATUS_ORDER
 * - Safe percentage calculation (no NaN/Infinity)
 * - Zero values shown (not hidden)
 */
import React from 'react';
import { STATUS_ORDER, safePercentage } from '../../hooks/useReportsDashboard';

// Status display configuration
const STATUS_CONFIG = {
  NEW: { label: 'New', color: '#3b82f6', bg: '#eff6ff' },
  TRIAGED: { label: 'Triaged', color: '#8b5cf6', bg: '#f5f3ff' },
  IN_TREATMENT: { label: 'In Treatment', color: '#f59e0b', bg: '#fffbeb' },
  VERIFICATION: { label: 'Verification', color: '#06b6d4', bg: '#ecfeff' },
  CLOSED: { label: 'Closed', color: '#10b981', bg: '#ecfdf5' },
  ARCHIVED: { label: 'Archived', color: '#6b7280', bg: '#f3f4f6' },
};

// Status Badge component (inline to avoid circular deps)
const StatusBadge = ({ status, size = 'sm' }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span 
      className={`status-badge status-badge-${size}`}
      style={{ color: config.color, backgroundColor: config.bg }}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </span>
  );
};

export const StatusBreakdownCard = ({ byStatus, totalRisks, onViewAll }) => {
  // Use STATUS_ORDER for consistent business ordering
  const statusEntries = STATUS_ORDER.map(status => [status, byStatus?.[status] ?? 0]);
  
  return (
    <div className="dashboard-card" data-testid="status-breakdown-card">
      <div className="breakdown-header">
        <h3>By Status</h3>
        {onViewAll && (
          <button className="view-all-link" onClick={onViewAll} data-testid="status-view-all">
            View All →
          </button>
        )}
      </div>
      <div className="status-breakdown">
        {statusEntries.map(([status, count]) => {
          const pct = safePercentage(count, totalRisks);
          const config = STATUS_CONFIG[status] || { color: '#6b7280' };
          
          return (
            <div key={status} className="status-row" data-testid={`status-row-${status}`}>
              <StatusBadge status={status} />
              <span className="status-count">{count}</span>
              <div className="status-bar">
                <div 
                  className="status-bar-fill" 
                  style={{ 
                    width: `${pct}%`,
                    backgroundColor: config.color
                  }} 
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusBreakdownCard;
